import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Container, Table } from 'react-bootstrap';
import { useAuth } from './auth/AuthContext';
import '../css/club-entry-summary.css';

const ACTIVE_ENTRY_STATUSES = new Set([0, 1, 4]);

function toMoney(value) {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(value || 0));
}

function toSummaryKey(entry) {
    const clubId = Number(entry?.clubId || 0);
    if (clubId > 0) return `club-${clubId}`;

    const fullName = String(entry?.clubName || '').trim().toLowerCase();
    const shortName = String(entry?.clubShortName || '').trim().toLowerCase();
    const clubCode = String(entry?.clubCode || '').trim().toLowerCase();
    return `${fullName}::${shortName}::${clubCode}`;
}

export function ClubEntrySummary() {
    const { getToken } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sortKey, setSortKey] = useState('fullName');
    const [sortDirection, setSortDirection] = useState('asc');

    useEffect(() => {
        let active = true;

        const loadEntries = async () => {
            setLoading(true);
            setError('');

            try {
                const token = getToken();
                const response = await fetch('/api/registration-desk/entries', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!response.ok) {
                    throw new Error(`Failed to load club summary (${response.status})`);
                }

                const payload = await response.json();
                if (active) {
                    setEntries(Array.isArray(payload) ? payload : []);
                }
            } catch (err) {
                if (active) {
                    setError(err.message || 'Failed to load club summary.');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadEntries();
        return () => {
            active = false;
        };
    }, [getToken]);

    const rows = useMemo(() => {
        const grouped = new Map();

        for (const entry of entries) {
            const status = Number(entry?.status);
            if (!ACTIVE_ENTRY_STATUSES.has(status)) continue;

            const key = toSummaryKey(entry);
            const fee = Number(entry?.entryFee || 0);
            const clubId = Number(entry?.clubId || 0);

            if (!grouped.has(key)) {
                grouped.set(key, {
                    clubId: clubId > 0 ? clubId : null,
                    fullName: String(entry?.clubName || '').trim() || '(Unknown Club)',
                    shortName: String(entry?.clubShortName || '').trim() || '-',
                    clubCode: String(entry?.clubCode || '').trim() || '-',
                    activeEntries: 0,
                    activeValue: 0
                });
            }

            const summary = grouped.get(key);
            summary.activeEntries += 1;
            summary.activeValue += Number.isFinite(fee) ? fee : 0;
        }

        return [...grouped.values()];
    }, [entries]);

    const sortedRows = useMemo(() => {
        const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
        const sorted = [...rows].sort((a, b) => {
            if (sortKey === 'activeEntries' || sortKey === 'activeValue') {
                return (Number(a[sortKey] || 0) - Number(b[sortKey] || 0)) * directionMultiplier;
            }

            const left = String(a[sortKey] || '');
            const right = String(b[sortKey] || '');
            return left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true }) * directionMultiplier;
        });

        return sorted;
    }, [rows, sortDirection, sortKey]);

    const totals = useMemo(() => {
        return rows.reduce((acc, row) => ({
            activeEntries: acc.activeEntries + row.activeEntries,
            activeValue: acc.activeValue + row.activeValue
        }), { activeEntries: 0, activeValue: 0 });
    }, [rows]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setSortKey(key);
        setSortDirection('asc');
    };

    const sortIndicator = (key) => {
        if (sortKey !== key) return '';
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    if (loading) {
        return <p><em>Loading...</em></p>;
    }

    return (
        <Container fluid>
            <h1>Club Entry Summary</h1>
            <p className="club-summary-subtitle">
                Active entries include Submitted, Accepted and Scratched crews.
            </p>

            {error && <Alert variant="danger">{error}</Alert>}
            {!error && rows.length === 0 && <Alert variant="info">No active entries found.</Alert>}

            {!error && rows.length > 0 && (
                <Table responsive bordered hover className="club-summary-table align-middle">
                    <thead>
                        <tr>
                            <th style={{ width: '32%' }} className="club-summary-sort-header" onClick={() => toggleSort('fullName')}>Full Club Name{sortIndicator('fullName')}</th>
                            <th style={{ width: '20%' }} className="club-summary-sort-header" onClick={() => toggleSort('shortName')}>Short Club Name{sortIndicator('shortName')}</th>
                            <th style={{ width: '14%' }} className="club-summary-sort-header" onClick={() => toggleSort('clubCode')}>Club Code{sortIndicator('clubCode')}</th>
                            <th style={{ width: '12%' }} className="text-end club-summary-sort-header" onClick={() => toggleSort('activeEntries')}>Active Entries{sortIndicator('activeEntries')}</th>
                            <th style={{ width: '14%' }} className="text-end club-summary-sort-header" onClick={() => toggleSort('activeValue')}>Active Entry Value{sortIndicator('activeValue')}</th>
                            <th style={{ width: '8%' }} className="text-center">Blade</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map(row => (
                            <tr key={`${row.clubId || 0}-${row.clubCode}-${row.fullName}`}>
                                <td>{row.fullName}</td>
                                <td>{row.shortName}</td>
                                <td>{row.clubCode}</td>
                                <td className="text-end">{row.activeEntries}</td>
                                <td className="text-end">{toMoney(row.activeValue)}</td>
                                <td className="text-center">
                                    {row.clubId ? (
                                        <span className="club-summary-blade-holder">
                                            <img
                                                src={`/api/clubs/${row.clubId}/blade`}
                                                alt={`${row.fullName} blade`}
                                                className="club-summary-blade"
                                                loading="lazy"
                                                onError={(event) => {
                                                    event.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        </span>
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th colSpan={3}>Totals</th>
                            <th className="text-end">{totals.activeEntries}</th>
                            <th className="text-end">{toMoney(totals.activeValue)}</th>
                            <th />
                        </tr>
                    </tfoot>
                </Table>
            )}
        </Container>
    );
}
